
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useBaseMfaState } from './useBaseMfaState';
import { handleMfaError } from '../types';

/**
 * Hook for MFA verification operations
 * 
 * Provides functionality to verify one-time passwords during the MFA authentication flow
 * 
 * @returns MFA verification functions and state
 */
export function useMfaVerification() {
  const { 
    factorId, 
    challengeId, 
    setIsLoading, 
    setIsMfaRequired, 
    setFactorId, 
    setChallengeId 
  } = useBaseMfaState();

  /**
   * Verify a one-time password (OTP) for MFA authentication
   * 
   * This function:
   * 1. Validates the OTP format
   * 2. Submits it to the authentication provider
   * 3. Updates authentication state on success
   * 4. Handles error cases with user feedback
   * 
   * @param otp - 6-digit one-time password from authenticator app
   * @returns Promise resolving to boolean indicating success
   */
  const verifyMfaOtp = async (otp: string): Promise<boolean> => {
    if (!factorId || !challengeId) {
      toast({
        title: "MFA Error",
        description: "No MFA challenge was initiated",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      setIsLoading(true);
      
      // Validate OTP format
      if (!otp || !/^\d{6}$/.test(otp)) {
        throw new Error("Invalid verification code format");
      }
      
      // Get current user for rate limiting
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Check rate limiting
      const { data: rateLimitOk, error: rateLimitError } = await supabase.rpc(
        'check_rate_limit',
        { 
          user_uuid: user.id, 
          action_name: 'mfa_verification',
          max_attempts: 5,
          window_minutes: 15
        }
      );
      
      if (rateLimitError || !rateLimitOk) {
        throw new Error("Too many verification attempts. Please try again later.");
      }
      
      // Use secure TOTP verification first (this prevents replay attacks)
      const { data: isValidCode, error: totpError } = await supabase.rpc(
        'verify_totp_code_secure',
        { 
          user_uuid: user.id,
          totp_code: otp
        }
      );
      
      if (totpError) {
        console.error('TOTP verification error:', totpError);
        throw new Error("Verification code validation failed");
      }
      
      if (!isValidCode) {
        throw new Error("Invalid verification code");
      }
      
      // If our secure verification passes, proceed with Supabase MFA verification
      const { data, error } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challengeId,
        code: otp,
      });
      
      if (error) throw error;
      
      setIsMfaRequired(false);
      setFactorId(null);
      setChallengeId(null);
      
      toast({
        title: "MFA Verified",
        description: "You have been successfully authenticated.",
      });
      
      return true;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('MFA verification failed:', mfaError);
      toast({
        title: "MFA Verification Failed",
        description: mfaError.message || "Invalid code. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    verifyMfaOtp
  };
}
